import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Home, LogOut, Heart, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Wellness({ user, onLogout }) {
  const navigate = useNavigate();
  const [gratitudeItems, setGratitudeItems] = useState(['', '', '']);
  const [gratitudeEntries, setGratitudeEntries] = useState([]);
  const [meditationDuration, setMeditationDuration] = useState(5);
  const [isMeditating, setIsMeditating] = useState(false);
  const [meditationTime, setMeditationTime] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchGratitudeEntries();
  }, []);

  useEffect(() => {
    let interval;
    if (isMeditating) {
      interval = setInterval(() => {
        setMeditationTime((prev) => {
          if (prev >= meditationDuration * 60) {
            setIsMeditating(false);
            handleMeditationComplete();
            return 0;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isMeditating, meditationDuration]);

  const fetchGratitudeEntries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/gratitude`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGratitudeEntries(response.data.entries);
    } catch (error) {
      console.error('Error fetching gratitude:', error);
    }
  };

  const handleGratitudeSubmit = async () => {
    const validItems = gratitudeItems.filter(item => item.trim() !== '');
    
    if (validItems.length === 0) {
      toast.error('En az bir şey yaz!');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/gratitude`,
        validItems,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('🙏 Minnettarlık kaydedildi!');
      setGratitudeItems(['', '', '']);
      fetchGratitudeEntries();
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const startMeditation = () => {
    setIsMeditating(true);
    setMeditationTime(0);
    toast.success('🧘 Meditasyon başladı!');
  };

  const stopMeditation = () => {
    setIsMeditating(false);
    setMeditationTime(0);
  };

  const handleMeditationComplete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/meditation`,
        null,
        { 
          params: { duration: meditationDuration },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('🎉 Meditasyon tamamlandı! +25 XP');
    } catch (error) {
      console.error('Error logging meditation:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-3xl">🧘</span>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Wellness</h1>
              <p className="text-sm text-gray-600">Öz bakım zamanı</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="home-btn"
              variant="outline"
              onClick={() => navigate('/')}
              className="gap-2 hover-lift"
            >
              <Home className="w-4 h-4" />
              Ana Sayfa
            </Button>
            <Button
              data-testid="logout-btn"
              variant="outline"
              onClick={onLogout}
              className="gap-2 hover-lift"
            >
              <LogOut className="w-4 h-4" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gratitude Journal */}
          <div className="bg-white rounded-2xl p-8 shadow-lg animate-scale-in">
            <div className="flex items-center gap-2 mb-6">
              <Heart className="w-6 h-6 text-pink-500" />
              <h2 className="text-2xl font-semibold text-gray-900">Minnettarlık Günlüğü</h2>
            </div>
            
            <p className="text-gray-600 mb-6">Bugün minnettar olduğun 3 şey nedir?</p>
            
            <div className="space-y-4 mb-6">
              {gratitudeItems.map((item, index) => (
                <div key={index}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {index + 1}. Şey
                  </label>
                  <Input
                    value={item}
                    onChange={(e) => {
                      const newItems = [...gratitudeItems];
                      newItems[index] = e.target.value;
                      setGratitudeItems(newItems);
                    }}
                    placeholder={`Örn: ${index === 0 ? 'Sağlığım' : index === 1 ? 'Ailem' : 'Dostlarım'}`}
                    className="w-full"
                  />
                </div>
              ))}
            </div>

            <Button
              onClick={handleGratitudeSubmit}
              disabled={loading}
              className="w-full py-6 text-lg font-semibold hover-lift"
            >
              {loading ? 'Kaydediliyor...' : '🙏 Kaydet (+20 XP)'}
            </Button>

            {/* Recent Entries */}
            {gratitudeEntries.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Son Kayıtlar</h3>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {gratitudeEntries.slice(0, 5).map((entry, idx) => (
                    <div key={idx} className="bg-pink-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-2">{entry.date}</p>
                      <ul className="space-y-1">
                        {entry.items.map((item, i) => (
                          <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-pink-500 mt-0.5">❤</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Meditation Timer */}
          <div className="bg-white rounded-2xl p-8 shadow-lg animate-scale-in" style={{ animationDelay: '0.1s' }}>
            <div className="flex items-center gap-2 mb-6">
              <Clock className="w-6 h-6 text-purple-500" />
              <h2 className="text-2xl font-semibold text-gray-900">Meditasyon</h2>
            </div>

            <div className="text-center">
              {!isMeditating ? (
                <>
                  <div className="text-8xl mb-6 animate-pulse-slow">🧘</div>
                  
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Süre Seç (Dakika)
                    </label>
                    <div className="flex gap-3 justify-center">
                      {[5, 10, 15, 20].map((duration) => (
                        <button
                          key={duration}
                          onClick={() => setMeditationDuration(duration)}
                          className={`px-6 py-3 rounded-xl font-semibold transition-all hover-grow ${
                            meditationDuration === duration
                              ? 'bg-purple-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {duration}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={startMeditation}
                    size="lg"
                    className="w-full py-6 text-lg font-semibold hover-lift"
                  >
                    🧘 Başla ({meditationDuration} dakika)
                  </Button>
                </>
              ) : (
                <>
                  <div className="text-8xl mb-6 animate-bounce-slow">🧘‍♀️</div>
                  
                  <div className="mb-8">
                    <div className="text-6xl font-bold text-purple-600 mb-4">
                      {formatTime(meditationTime)}
                    </div>
                    <p className="text-gray-600">Derin nefes al, rahatlat...</p>
                  </div>

                  <div className="w-full bg-gray-200 rounded-full h-4 mb-6 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full progress-bar"
                      style={{ width: `${(meditationTime / (meditationDuration * 60)) * 100}%` }}
                    />
                  </div>

                  <Button
                    onClick={stopMeditation}
                    variant="outline"
                    size="lg"
                    className="w-full py-6 text-lg font-semibold"
                  >
                    ⏸️ Durdur
                  </Button>
                </>
              )}
            </div>

            <div className="mt-8 p-4 bg-purple-50 rounded-xl">
              <h4 className="font-semibold mb-2">💡 İpucu</h4>
              <p className="text-sm text-gray-700">
                Meditasyon sırasında derin nefes al, düşüncelerini gözlemle ve rahatlat. Günlük 5 dakika bile fark yaratır!
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
